import React, { useCallback, useEffect, useRef, useState } from "react";
import "./App.css";
import "./components/LiveTradingHeader.css";
import AninaTradeHomePage from "./pages/AninaTradeHomePage.js";
import { useAuth } from "./auth/AuthContext";
import AskNinaWorkspace from "./components/AskNinaWorkspace.js";
import AcademyHome from "./academy/AcademyHome.js";
import GuidedTourOverlay from "./components/GuidedTourOverlay.js";
import GuidedNavigation from "./components/GuidedNavigation.js";
import LiveChartWorkspace from "./components/LiveChartWorkspace.jsx";
import LiveMarketOverview from "./components/LiveMarketOverview.js";
import NinaLearningLog from "./components/NinaLearningLog.js";
import NinaMissionPanel from "./components/NinaMissionPanel";
import NinaScannerWorkspace from "./components/NinaScannerWorkspace.js";
import NinaTradePlan from "./components/NinaTradePlan.js";
import PaperAutoTradeWorkspace from "./components/PaperAutoTradeWorkspace";
import PaperTradingWorkspace from "./components/PaperTradingWorkspace";
import TaxFormsPage from "./components/TaxFormsPage.js";
import UnifiedPortfolioWorkspace from "./components/UnifiedPortfolioWorkspace.js";
import UserProfilePage from "./components/UserProfilePage.js";
import { NinaMemoryProvider } from "./context/NinaMemoryContext";
import { validateScannerReviewHandoff } from "./scanner/NinaScannerReviewHandoff.js";
import TradingCenter from "./tradingCenter/TradingCenter.jsx";
import {
  TRADING_MODE_IDS,
  resolveTradingModeFromLegacyPage,
} from "./tradingCenter/tradingModes";
import "./styles/theme.css";

const MISSION_PAGE_IDS = ["nina-status", "watchlist", "market-clock"];
const LEGACY_TRADING_PAGE_IDS = ["live", "paper-trading", "paper-autotrade"];

const GUEST_EXPLORE_PAGE_IDS = new Set([
  "scanner",
  "nina",
  "academy",
  "portfolio",
]);

function resolveInitialGuestPage(user) {
  if (user) return "live";

  const requestedPage = window.history.state?.aninaGuestPage;

  if (LEGACY_TRADING_PAGE_IDS.includes(requestedPage)) {
    return "live";
  }

  if (GUEST_EXPLORE_PAGE_IDS.has(requestedPage)) {
    return requestedPage;
  }

  return "live";
}

function resolveInitialGuestTradingMode(user) {
  if (user) return TRADING_MODE_IDS.LIVE;

  return resolveTradingModeFromLegacyPage(
    window.history.state?.aninaGuestPage
  );
}

const GUIDED_TOUR_PAGE_BY_STEP = {
  "AI Live Coach": "live",
  "Quick Trade": "live",
  "AI Decision": "live",
  "Professional Chart": "live",
  "AI SmartFlow": "live",
  "Market Structure Analysis": "live",
  "Account Summary": "live",
  "Trade Management": "live",
  Scanner: "scanner",
  Portfolio: "portfolio",
  Academy: "academy",
  Journal: "journal",
  "Tax Forms": "tax-forms",
  Settings: "settings",
};

const DENSITY_OPTIONS = [
  { id: "compact", label: "Compact", description: "Maximum information with tighter spacing." },
  { id: "standard", label: "Standard", description: "Balanced spacing and comfortable readability." },
  { id: "comfortable", label: "Comfortable", description: "Larger controls, cards, and interface text." },
];

function App() {
  const { user, ninaAccess } = useAuth();
  const [activePage, setActivePage] = useState(
    () => resolveInitialGuestPage(user)
  );
  const [tradingMode, setTradingMode] = useState(
    () => resolveInitialGuestTradingMode(user)
  );
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [scannerReview, setScannerReview] = useState(null);
  const [scannerReviewError, setScannerReviewError] = useState("");
  const [scannerOrigin, setScannerOrigin] = useState(null);
  const [scannerSeed, setScannerSeed] = useState(null);
  const [scannerReturnScrollY, setScannerReturnScrollY] = useState(0);
  const [scannerMounted, setScannerMounted] = useState(false);
  const scannerReturnPendingRef = useRef(false);
  const [guidedTourActive, setGuidedTourActive] = useState(false);
  const [guidedTourReturnPage, setGuidedTourReturnPage] = useState("live");
  const [liveRibbonDecision, setLiveRibbonDecision] = useState(null);
  const [liveTradePlan, setLiveTradePlan] = useState(null);
  const [liveSymbol, setLiveSymbol] = useState(() => {
    try {
      return localStorage.getItem("aninaSelectedSymbol") || "PLUG";
    } catch (error) {
      return "PLUG";
    }
  });
  const [workspaceDensity, setWorkspaceDensity] = useState(() => {
    try {
      return localStorage.getItem("aninaWorkspaceDensity") || "standard";
    } catch (error) {
      return "standard";
    }
  });

  useEffect(() => {
    const handleSymbolChange = (event) => {
      const nextSymbol = String(event.detail?.symbol || "")
        .trim()
        .toUpperCase();
      if (!/^[A-Z][A-Z0-9.-]{0,9}$/.test(nextSymbol)) return;
      setLiveSymbol(nextSymbol);
    };

    window.addEventListener("nina:symbol-change", handleSymbolChange);
    return () => window.removeEventListener("nina:symbol-change", handleSymbolChange);
  }, []);

  useEffect(() => {
    if (activePage === "scanner") setScannerMounted(true);
  }, [activePage]);

  useEffect(() => {
    if (activePage !== "scanner" || !scannerReturnPendingRef.current) return;

    scannerReturnPendingRef.current = false;
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: scannerReturnScrollY, behavior: "auto" });
    });
  }, [activePage, scannerReturnScrollY]);

  const handleDensityChange = (density) => {
    setWorkspaceDensity(density);
    try {
      localStorage.setItem("aninaWorkspaceDensity", density);
    } catch (error) {
      // The setting remains available for the current session.
    }
  };

  const openScannerReview = (review) => {
    const validation = validateScannerReviewHandoff(review);
    if (!validation.valid) {
      setScannerReviewError(validation.error);
      return { ok: false, error: validation.error };
    }

    let warning = "";
    try {
      localStorage.setItem("aninaSelectedSymbol", validation.value.symbol);
    } catch (error) {
      warning = "The symbol is available for this review, but this browser denied saving it for later.";
    }

    window.dispatchEvent(
      new CustomEvent("nina:symbol-change", {
        detail: { symbol: validation.value.symbol },
      }),
    );
    setScannerReturnScrollY(window.scrollY);
    setLiveSymbol(validation.value.symbol);
    setScannerReview(validation.value);
    setScannerOrigin(null);
    setScannerSeed(null);
    setScannerReviewError(warning);
    setTradingMode(TRADING_MODE_IDS.LIVE);
    setActivePage("live");
    setMobileNavigationOpen(false);
    return { ok: true, warning };
  };

  const returnToScanner = () => {
    scannerReturnPendingRef.current = true;
    if (scannerOrigin?.mode) {
      window.dispatchEvent(
        new CustomEvent("nina:scanner-restore-mode", {
          detail: { mode: scannerOrigin.mode },
        }),
      );
    }
    setScannerReview(null);
    setScannerReviewError("");
    setScannerSeed(null);
    setActivePage("scanner");
    setMobileNavigationOpen(false);
    setScannerOrigin(null);
  };

  const handleGuidedTourStepChange = useCallback(({ title }) => {
    const nextPage = GUIDED_TOUR_PAGE_BY_STEP[title];
    if (!nextPage) return;

    if (nextPage === "live") setTradingMode(TRADING_MODE_IDS.LIVE);
    setActivePage(nextPage);
    setMobileNavigationOpen(false);
  }, []);

  const exitGuidedTour = () => {
    setGuidedTourActive(false);
    setActivePage(guidedTourReturnPage);
    setMobileNavigationOpen(false);
  };

  const navigateTo = (page, context = {}) => {
    if (page === "guided-tour") {
      setGuidedTourReturnPage(activePage);
      setGuidedTourActive(true);
      setMobileNavigationOpen(false);
      return;
    }

    const scannerHandoff = context?.scannerHandoff;
    if (page === "live" && scannerHandoff) {
      const handoffSymbol = String(scannerHandoff.symbol || "")
        .trim()
        .toUpperCase();
      if (/^[A-Z][A-Z0-9.-]{0,9}$/.test(handoffSymbol)) {
        setLiveSymbol(handoffSymbol);
        try {
          localStorage.setItem("aninaSelectedSymbol", handoffSymbol);
        } catch (error) {
          // Current-session handoff still works if browser storage is unavailable.
        }
        window.dispatchEvent(
          new CustomEvent("nina:symbol-change", {
            detail: { symbol: handoffSymbol },
          }),
        );
      }

      setScannerReturnScrollY(window.scrollY);
      setScannerReview(null);
      setScannerReviewError("");
      setScannerOrigin(scannerHandoff.origin || {
        mode: scannerHandoff.scannerKind || "day",
        label: "Scanner",
        status: "LIVE",
      });
      setScannerSeed(scannerHandoff.seed || null);
      setGuidedTourActive(false);
      setTradingMode(TRADING_MODE_IDS.LIVE);
      setActivePage("live");
      setMobileNavigationOpen(false);
      return;
    }

    if (LEGACY_TRADING_PAGE_IDS.includes(page)) {
      scannerReturnPendingRef.current = false;
      setScannerReview(null);
      setScannerReviewError("");
      setScannerOrigin(null);
      setScannerSeed(null);
      setGuidedTourActive(false);
      setTradingMode(resolveTradingModeFromLegacyPage(page));
      setActivePage("live");
      setMobileNavigationOpen(false);
      return;
    }

    scannerReturnPendingRef.current = false;
    setScannerReview(null);
    setScannerReviewError("");
    setScannerOrigin(null);
    setScannerSeed(null);
   setGuidedTourActive(false);

if (page === "scanner") {
  setScannerMounted(true);
}

setActivePage(page);
setMobileNavigationOpen(false);
  };

  const analyzeLiveSymbol = () => {
    const nextSymbol = liveSymbol.trim().toUpperCase();
    if (!nextSymbol) return;

    setLiveSymbol(nextSymbol);
    setScannerSeed((current) =>
      String(current?.symbol || "").toUpperCase() === nextSymbol ? current : null
    );
    try {
      localStorage.setItem("aninaSelectedSymbol", nextSymbol);
    } catch (error) {
      // Analysis remains available for the current session.
    }
    window.dispatchEvent(
      new CustomEvent("nina:symbol-change", {
        detail: { symbol: nextSymbol },
      }),
    );
  };

  const renderSettingsPage = () => (
    <section className="settingsPage">
      <div className="settingsPageHeader">
        <span>ANINATRADE APPEARANCE</span>
        <h2> Settings</h2>
        <p>Customize how much information appears across your trading cockpit.</p>
      </div>
      <div className="settingsCard">
        <div className="settingsSectionHeader">
          <div>
            <span>WORKSPACE DENSITY</span>
            <h3>Interface Size</h3>
            <p>This changes the size of the AninaTrade interface, but it does not resize the professional chart.</p>
          </div>
          <strong>{workspaceDensity.charAt(0).toUpperCase() + workspaceDensity.slice(1)}</strong>
        </div>
        <div className="densityOptionGrid">
          {DENSITY_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={workspaceDensity === option.id ? "densityOption activeDensityOption" : "densityOption"}
              onClick={() => handleDensityChange(option.id)}
            >
              <span className="densityRadio">{workspaceDensity === option.id ? "[x]" : "[ ]"}</span>
              <div>
                <strong>{option.label}</strong>
                <p>{option.description}</p>
              </div>
            </button>
          ))}
        </div>
        <div className="chartDensityNotice">
          <span></span>
          <div>
            <strong>Chart size remains independent</strong>
            <p>Candles, chart labels, volume, targets, and price lines keep their own professional sizing.</p>
          </div>
        </div>
      </div>
    </section>
  );

  const scannerBackLabel = scannerOrigin?.label
    ? `<- Back to ${scannerOrigin.label} Scanner`
    : "<- Back to Scanner Results";

  const ribbonHasAuthenticatedAnalysis =
    Boolean(liveRibbonDecision?.decision) &&
    liveRibbonDecision?.status !== "DATA BLOCKED";
  const ribbonStatus = liveRibbonDecision?.status || "NOT READY";
  const ribbonDecision =
    liveRibbonDecision?.decision || "WAIT - AUTHENTICATED ANALYSIS UNAVAILABLE";
  const ribbonConfidence = Number.isFinite(liveRibbonDecision?.confidence)
    ? `${liveRibbonDecision.confidence}%`
    : "Unavailable";
  const ribbonRisk = liveRibbonDecision?.risk || "Unavailable";

  const renderLiveTradingMode = (tradingCenterControls = null, readOnlyMode = false) => (
    <section className="scannerTradeReviewPage" aria-labelledby="live-review-title">
      <header className="liveCommandHeader">
        <h1 id="live-review-title" className="visuallyHidden">
          {scannerReview
            ? "Live Trading / Pit #3 Review"
            : scannerOrigin
            ? "Live Trading / Scanner Handoff"
            : "Live Trading Command Center"}
        </h1>

        <div className="liveBrandIdentity" aria-label="AninaTrade identity">
          <span>A</span>
          <div>
            <strong>AninaTrade</strong>
            <small>AI Trading Operating System</small>
          </div>
        </div>

        {(scannerOrigin || scannerReview) && (
          <button
            type="button"
            className="scannerReturnButton"
            onClick={returnToScanner}
          >
            {scannerBackLabel}
          </button>
        )}

        <form
          className="liveSymbolSearch"
          onSubmit={(event) => {
            event.preventDefault();
            analyzeLiveSymbol();
          }}
        >
          <label htmlFor="live-symbol-search">Symbol</label>
          <span aria-hidden="true"></span>
          <input
            id="live-symbol-search"
            value={scannerReview?.symbol || liveSymbol}
            onChange={(event) => setLiveSymbol(event.target.value)}
            disabled={Boolean(scannerReview)}
            aria-label="Symbol search"
          />
          <button type="submit" disabled={Boolean(scannerReview)}>Analyze</button>
        </form>

        <div className="liveHeaderActions">
          <span className="liveStatus"> LIVE</span>
          <button type="button" onClick={() => navigateTo("nina")}> AI Assistant</button>
        </div>

        <div className="liveSessionStatusBar" aria-label="Live session status">
          <strong className={ribbonHasAuthenticatedAnalysis ? "positive" : ""}>
             {ribbonHasAuthenticatedAnalysis
              ? "Authenticated analysis"
              : "Awaiting authenticated analysis"}
          </strong>
          <span> AI Assistant {ribbonStatus}</span>
          <span> {ribbonDecision}</span>
          <span className="warning"> Confidence {ribbonConfidence}</span>
          <span> Risk {ribbonRisk}</span>
        </div>
      </header>

      <div className="liveIntelligenceRibbon">
        <LiveMarketOverview />
      </div>

      {scannerReviewError && <div className="scannerError" role="alert">{scannerReviewError}</div>}
      <LiveChartWorkspace
        scannerReview={scannerReview}
        scannerSeed={scannerSeed}
        isActive={activePage === "live"}
        tradingCenterControls={tradingCenterControls}
        onLiveDecisionChange={setLiveRibbonDecision}
        onTradePlanChange={setLiveTradePlan}
        readOnlyMode={readOnlyMode}
      />
      {(
        <details className="liveSupplementalPanel">
          <summary>Open detailed trade plan</summary>
          <NinaTradePlan scannerReview={scannerReview} />
        </details>
      )}
    </section>
  );

  const renderTradingModeWorkspace = (policy, tradingCenterControls = null) => {
    if (policy.id === TRADING_MODE_IDS.PAPER) return <PaperTradingWorkspace sharedSymbol={liveSymbol} />;
    if (policy.id === TRADING_MODE_IDS.PAPER_AUTO) {
  return <PaperAutoTradeWorkspace sharedTradePlan={liveTradePlan} />;
}
    return renderLiveTradingMode(tradingCenterControls);
  };

  return (
    <NinaMemoryProvider>
        <div
          className={`App workspaceDensity-${workspaceDensity}${activePage === "live" ? " liveTradingApp" : ""}${activePage === "dashboard" ? " dashboardApp" : ""}${activePage === "scanner" ? " scannerApp" : ""}${activePage === "portfolio" || MISSION_PAGE_IDS.includes(activePage) ? " missionWorkspaceApp" : ""}`}
        >
          <aside
            className={`sideNav${activePage === "live" ? " liveTradingSideNav" : ""}${activePage === "dashboard" ? " dashboardSideNav" : ""}${activePage === "scanner" ? " scannerSideNav" : ""}${activePage === "portfolio" || MISSION_PAGE_IDS.includes(activePage) ? " missionWorkspaceSideNav" : ""}${mobileNavigationOpen ? " sidebarNavigationOpen" : ""}`}
          >
            <h2
  style={{
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }}
>
  <svg
    width="26"
    height="26"
    viewBox="0 0 32 32"
    aria-hidden="true"
  >
    <path
      d="M15.8 3 5 25h5.2l7.9-16.2L15.8 3Z"
      fill="#00d8c9"
    />
    <path
      d="m20.2 8.2-8.4 16.9H17l5.7-11.6-2.5-5.3Z"
      fill="#00a99d"
    />
    <path
      d="m23.6 15.5-4.8 9.6H27l-3.4-9.6Z"
      fill="#00d8c9"
    />
  </svg>
  <span>AninaTrade</span>
</h2>
            <button
              type="button"
              className="mobileNavigationToggle"
              aria-expanded={mobileNavigationOpen}
              aria-controls="anina-primary-navigation"
              onClick={() => setMobileNavigationOpen((current) => !current)}
            >
              <span aria-hidden="true"></span>
              Menu
            </button>
            <GuidedNavigation activePage={activePage} onNavigate={navigateTo} />
          </aside>

          <main
            className={`mainContent${activePage === "live" ? " liveTradingMain" : ""}${activePage === "scanner" ? " scannerMain" : ""}${activePage === "portfolio" || MISSION_PAGE_IDS.includes(activePage) ? " missionWorkspaceMain" : ""}`}
          >
            {activePage === "dashboard" && <AninaTradeHomePage onNavigate={navigateTo} />}
            {activePage === "nina" && (
  <section className="ninaUnifiedWorkspace">
    <AskNinaWorkspace
      liveDecision={liveRibbonDecision}
      guestMode={!user}
    />

    <details className="ninaMemoryContinuation">
      <summary>
        <span>AI MEMORY</span>

        <strong>
          Personal Strategy & Learning
        </strong>

        <small>
          The AI Assistant learns this user's trading rules,
          decisions, outcomes, and lessons.
        </small>
      </summary>

      <NinaLearningLog />
    </details>
  </section>
)}


            <div hidden={activePage !== "live"} data-testid="persistent-trading-center-host">
              <TradingCenter
                mode={tradingMode}
                featureAccess={ninaAccess?.feature_access || {}}
                onModeChange={(nextMode) => {
                  setScannerReview(null);
                  setScannerReviewError("");
                  setScannerOrigin(null);
                  setScannerSeed(null);
                  setTradingMode(nextMode);
                }}
                renderMode={renderTradingModeWorkspace}
                renderReadOnlyLive={() => renderLiveTradingMode(null, true)}
              />
            </div>

            <div hidden={activePage !== "scanner"} data-testid="persistent-scanner-host">
  {(scannerMounted || activePage === "scanner") && (
    <NinaScannerWorkspace
      onOpenTradeReview={openScannerReview}
      featureAccess={ninaAccess?.feature_access || {}}
      isActive={true}
    />
  )}
</div>
            {activePage === "portfolio" && <UnifiedPortfolioWorkspace />}
            {activePage === "profile" && <UserProfilePage />}
            {MISSION_PAGE_IDS.includes(activePage) && <NinaMissionPanel section={activePage} />}
            {activePage === "academy" && <AcademyHome />}
            {activePage === "journal" && (
              <section className="settingsPage" aria-labelledby="trade-journal-status-title">
                <div className="settingsPageHeader">
                  <span>BROKER-BACKED HISTORY ONLY</span>
                  <h2 id="trade-journal-status-title">Trade Journal</h2>
                  <p>
                    Trade journal analytics will appear here after authenticated
                    brokerage trade-history synchronization is connected.
                  </p>
                </div>
              </section>
            )}
            {activePage === "tax-forms" && <TaxFormsPage />}
            {activePage === "settings" && renderSettingsPage()}
          </main>

          {guidedTourActive && (
            <GuidedTourOverlay
              onExit={exitGuidedTour}
              onStepChange={handleGuidedTourStepChange}
            />
          )}
        </div>
    </NinaMemoryProvider>
  );
}

export default App;
